import { useMutationState } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { validateAmount } from '../lib/amountError';
import { createOrderDataDto, validateOrderFormData } from '../lib/orderHelpers';
import { getNonceInNanoseconds, nowInSeconds } from '../lib/time';
import { OrderSide } from '../types/generated';
import { OrderType, RawOrderFormData } from '../types/orders';
import { useDryRun } from './useDryRunWs';
import { getOrderMutationKey, useOrderMutation } from './useOrderMutation';
import { Position } from './usePositions';
import { ProductType } from './useProducts';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';

interface ClosePositionParams {
  position: Position | undefined;
  amount: bigint | undefined;
  price: bigint | undefined;
  type: OrderType;
  postOnly: boolean;
  product: ProductType | undefined;
}

export const useClosePositionMutation = ({
  position,
  amount,
  price,
  type,
  postOnly,
  product,
}: ClosePositionParams) => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { dryRun, result: dryRunResult, error: dryRunError, reset } = useDryRun();

  const amountSizeErrors = product && validateAmount(amount || 0n, product);

  // Construct close order params once for both dry-run and order submission.
  const closeOrderParams = useMemo(() => {
    if (!amount || (type === 'LIMIT' && !price)) {
      return null;
    }

    return {
      side: position?.side === OrderSide.BUY ? OrderSide.SELL : OrderSide.BUY,
      price: type === 'LIMIT' ? price : undefined,
      amount,
      type,
      reduceOnly: true,
      close: false,
      postOnly: type === 'LIMIT' ? postOnly : undefined,
    } satisfies RawOrderFormData;
  }, [amount, type, price, position?.side, postOnly]);

  useEffect(() => {
    if (!closeOrderParams) {
      reset();
      return;
    }

    if (!selectedSubaccount || !product || amountSizeErrors) {
      return;
    }

    const validatedOrder = validateOrderFormData(closeOrderParams);
    if (!validatedOrder) {
      return;
    }

    const data = createOrderDataDto({
      product,
      subaccountName: selectedSubaccount.name,
      sender: selectedSubaccount.account,
      nonce: getNonceInNanoseconds().toString(),
      signedAt: nowInSeconds(),
      ...validatedOrder,
    });

    dryRun({ data }, product);
  }, [selectedSubaccount, dryRun, amountSizeErrors, closeOrderParams, product, reset]);

  const closing = true;
  const { mutate } = useOrderMutation(product, closing);
  const isClosingPosition =
    useMutationState({
      filters: {
        mutationKey: getOrderMutationKey({ productId: product?.id, closing }),
        status: 'pending',
      },
    }).length > 0;

  const closePosition = useCallback(() => {
    if (!closeOrderParams) return;

    const validatedOrder = validateOrderFormData(closeOrderParams);
    if (!validatedOrder) {
      return;
    }

    mutate(validatedOrder);
  }, [mutate, closeOrderParams]);

  return {
    isClosingPosition,
    dryRunResult,
    dryRunError,
    closePosition,
  };
};
